import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Alert,
  Button,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Movie as MovieIcon,
  Error as ErrorIcon,
  HourglassTop as HourglassTopIcon,
  Pending as PendingIcon,
  CloudSync as CloudSyncIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { surface, gradients } from '../../theme';
import RenderProgress from './RenderProgress';

export default function RenderSection({
  doc,
  rendering,
  handleRender,
  renderActive,
  /** When set, the render button stays disabled (e.g. AI Stories needs footage folders). */
  renderBlockedReason = null,
}) {
  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 2,
        background: surface.glass,
        boxShadow: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <MovieIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant='h6' sx={{ fontWeight: 700, flex: 1 }}>
          Step 3: Render
        </Typography>
        {doc?.renderStatus?.status === 'completed' && (
          <Chip
            icon={<CheckCircleIcon />}
            label='Completed'
            color='success'
            size='small'
            variant='outlined'
          />
        )}
        {doc?.renderStatus?.status === 'queued' && (
          <Chip
            icon={<PendingIcon />}
            label='In Queue'
            color='default'
            size='small'
            variant='outlined'
          />
        )}
        {doc?.renderStatus?.status === 'setting_up' && (
          <Chip
            icon={<CloudSyncIcon />}
            label='Setting Up'
            color='info'
            size='small'
            variant='outlined'
          />
        )}
        {(doc?.renderStatus?.status === 'rendering' ||
          doc?.renderStatus?.status === 'finalizing') && (
          <Chip
            icon={<HourglassTopIcon />}
            label={
              doc.renderStatus.status === 'rendering' ? 'Rendering' : 'Finalizing'
            }
            color='warning'
            size='small'
            variant='outlined'
          />
        )}
        {doc?.renderStatus?.status === 'failed' && (
          <Chip
            icon={<ErrorIcon />}
            label='Failed'
            color='error'
            size='small'
            variant='outlined'
          />
        )}
      </Box>
      {doc?.renderStatus?.status === 'failed' && doc.renderStatus.error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {doc.renderStatus.error}
        </Alert>
      )}
      <RenderProgress
        renderStatus={doc?.renderStatus}
        voiceOverDuration={doc?.voiceOverDuration}
      />
      {doc?.renderStatus?.status === 'completed' && doc?.videoUrl && (
        <Box sx={{ mb: 2 }}>
          <video
            controls
            src={doc.videoUrl}
            style={{
              width: '100%',
              aspectRatio: '16/9',
              borderRadius: 8,
              background: '#000',
            }}
          />
        </Box>
      )}
      <Button
        variant='contained'
        startIcon={
          rendering ? (
            <CircularProgress size={18} color='inherit' />
          ) : doc?.renderStatus?.status === 'failed' ? (
            <ReplayIcon />
          ) : (
            <MovieIcon />
          )
        }
        onClick={handleRender}
        disabled={
          rendering ||
          renderActive ||
          !doc?.voiceOverObjectName ||
          Boolean(renderBlockedReason)
        }
        sx={{
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 2,
          background: gradients.secondary,
          '&:hover': { background: gradients.secondaryHover },
        }}
      >
        {renderActive
          ? 'Rendering...'
          : doc?.renderStatus?.status === 'failed'
            ? 'Retry'
            : doc?.renderStatus?.status === 'completed'
              ? 'Re-render Video'
              : 'Render Video'}
      </Button>
      {!doc?.voiceOverObjectName && (
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1 }}>
          Upload a voice over first to enable rendering.
        </Typography>
      )}
      {doc?.voiceOverObjectName && renderBlockedReason && (
        <Typography variant='caption' color='warning.main' sx={{ display: 'block', mt: 1 }}>
          {renderBlockedReason}
        </Typography>
      )}
    </Paper>
  );
}
